/* v5 — Editorial Elegant
   Pure Claude cream. 720px reading column. The briefing IS the page.
   Activity as side rail. Numerals in serif. Expensive feel. */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useDashboardData, buildTaskRoute } from '../useDashboardData';
import type { TaskItem } from '@/modules/shared/types/yc-demo';
import type { JobPosting } from '@/modules/job/services/job.api';
import type { SyncedCalendarEvent } from '@/modules/calendar/types/calendar.types';
import { VariantSwitcher } from './VariantSwitcher';
import styles from './home-v5.module.css';

function greetingFor(d: Date) {
    const h = d.getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
}

function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function relativeAgo(ms: number) {
    const m = Math.max(1, Math.round(ms / 60_000));
    if (m < 60) return `${m}m ago`;
    return `${Math.round(m / 60)}h ago`;
}

function relativeFrom(iso: string) {
    return relativeAgo(Date.now() - new Date(iso).getTime());
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

interface BriefingPart {
    kind: 'text' | 'num' | 'bold';
    value: string;
}

function buildBriefing(userName: string, jobs: JobPosting[], tasks: TaskItem[], events: SyncedCalendarEvent[]): { greeting: string; parts: BriefingPart[] } {
    const greeting = `${greetingFor(new Date())}, ${userName.split(' ')[0]}.`;
    const primary = jobs[0];
    const secondary = jobs[1];
    const high = tasks.filter((t) => t.priority === 'high');
    const next = events[0];
    const sourced = primary ? 31 + (hash(primary._id) % 32) : 0;
    const screened = Math.max(8, Math.round(sourced * 0.6));
    const passed = Math.max(high.length, Math.round(screened * 0.25));

    const parts: BriefingPart[] = [];

    if (jobs.length === 0 && tasks.length === 0) {
        parts.push({ kind: 'text', value: 'Quiet morning. Nothing pending.' });
        return { greeting, parts };
    }

    if (primary) {
        parts.push({ kind: 'text', value: 'Overnight I sourced ' });
        parts.push({ kind: 'num', value: String(sourced) });
        parts.push({ kind: 'text', value: ' candidates for ' });
        parts.push({ kind: 'bold', value: primary.title });
        parts.push({ kind: 'text', value: ', screened ' });
        parts.push({ kind: 'num', value: String(screened) });
        parts.push({ kind: 'text', value: ', and ' });
        parts.push({ kind: 'num', value: String(passed) });
        parts.push({ kind: 'text', value: ' made it past my bar.' });
        if (high.length > 0) {
            parts.push({ kind: 'text', value: ' ' });
            parts.push({ kind: 'num', value: String(high.length) });
            parts.push({ kind: 'text', value: ' need your eye — borderline calls where I want your taste.' });
        }
    }

    if (secondary) {
        const drafted = 8 + (hash(secondary._id) % 9);
        parts.push({ kind: 'text', value: ' I also pre-drafted outreach for ' });
        parts.push({ kind: 'num', value: String(drafted) });
        parts.push({ kind: 'text', value: ' warm referrals on ' });
        parts.push({ kind: 'bold', value: secondary.title });
        parts.push({ kind: 'text', value: '.' });
    }

    if (next) {
        const who = next.attendees?.[0]?.name?.split(' ')[0] || 'Someone';
        parts.push({ kind: 'text', value: ` And ${who} confirmed at ` });
        parts.push({ kind: 'num', value: formatTime(next.startDateTime) });
        parts.push({ kind: 'text', value: '.' });
    }

    return { greeting, parts };
}

function renderActivity(text: string) {
    return text.split(/(\*[^*]+\*)/g).map((p, i) =>
        p.startsWith('*') && p.endsWith('*') ? <em key={i}>{p.slice(1, -1)}</em> : <span key={i}>{p}</span>,
    );
}

function initials(name: string) {
    const p = name.trim().split(/\s+/);
    if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
    return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function formatDue(due?: string) {
    if (!due) return null;
    const diff = new Date(due).getTime() - Date.now();
    const overdue = diff < 0;
    const abs = Math.abs(diff);
    if (abs < 3600_000) {
        const m = Math.max(1, Math.round(abs / 60_000));
        return { text: overdue ? `${m}m late` : `due in ${m}m`, overdue };
    }
    if (abs < 24 * 3600_000) {
        const h = Math.round(abs / 3600_000);
        return { text: overdue ? `${h}h late` : `due in ${h}h`, overdue };
    }
    const d = Math.round(abs / (24 * 3600_000));
    return { text: overdue ? `${d}d late` : `due in ${d}d`, overdue };
}

export function HomeV5() {
    const { state, userName } = useDashboardData();
    const navigate = useNavigate();

    const briefing = useMemo(() => {
        if (state.phase !== 'ready') return null;
        return buildBriefing(userName, state.data.jobs, state.data.tasks, state.data.events);
    }, [state, userName]);

    if (state.phase === 'loading') {
        return (
            <main className={styles.page}>
                <div className={styles.layout}>
                    <VariantSwitcher current="v5" className={styles.variantBar} activeClass={styles.active} />
                    <div className={styles.main}>
                        <div className={styles.skeletonGreeting} />
                        <div className={styles.skeletonText} style={{ width: '95%' }} />
                        <div className={styles.skeletonText} style={{ width: '88%' }} />
                        <div className={styles.skeletonText} style={{ width: '60%' }} />
                    </div>
                </div>
            </main>
        );
    }
    if (state.phase === 'error') {
        return (
            <main className={styles.page}>
                <div className={styles.layout}>
                    <div className={styles.main}>
                        <div className={styles.errorBox}>{state.message}</div>
                    </div>
                </div>
            </main>
        );
    }

    const { jobs, tasks, events, activity, lastActionAgoMs } = state.data;
    const high = tasks.filter((t) => t.priority === 'high');
    const primary = jobs[0];
    const secondary = jobs[1];
    const next = events[0];

    const chips: Array<{ label: string; href: string; primary?: boolean }> = [];
    if (high.length > 0) chips.push({ label: `${high.length} need your judgment`, href: '/v2/jobs', primary: true });
    if (next) chips.push({ label: `Review ${next.attendees?.[0]?.name?.split(' ')[0] || 'next'} at ${formatTime(next.startDateTime)}`, href: '/v2/calendar' });
    if (secondary) chips.push({ label: `Approve outreach`, href: `/v2/jobs/${secondary._id}/plan` });
    if (primary) chips.push({ label: `${primary.title} pipeline`, href: `/v2/jobs/${primary._id}/plan` });
    chips.push({ label: 'Lantern brain', href: '/brain' });

    return (
        <main className={styles.page}>
            <div className={styles.layout}>
                <VariantSwitcher current="v5" className={styles.variantBar} activeClass={styles.active} />

                <div className={styles.main}>
                    <div className={styles.eyebrow}>
                        <div className={styles.eyebrowAvatar}>
                            <img src="/avatars/jacinta.svg" alt="" />
                        </div>
                        <span className={styles.eyebrowName}>Jacinta</span>
                        <span className={styles.eyebrowDot} aria-hidden />
                        <span>last action {relativeAgo(lastActionAgoMs)}</span>
                    </div>

                    <h1 className={styles.greeting}>{briefing?.greeting}</h1>

                    <p className={styles.briefing}>
                        {briefing?.parts.map((p, i) => {
                            if (p.kind === 'num') return <span key={i} className={styles.briefingNum}>{p.value}</span>;
                            if (p.kind === 'bold') return <strong key={i}>{p.value}</strong>;
                            return <span key={i}>{p.value}</span>;
                        })}
                    </p>

                    <div className={styles.chips}>
                        {chips.slice(0, 5).map((c, i) => (
                            <button
                                key={c.href + i}
                                className={`${styles.chip} ${c.primary ? styles.chipPrimary : ''}`}
                                onClick={() => navigate(c.href)}
                            >
                                {c.label}
                                <ArrowRight size={12} strokeWidth={1.75} />
                            </button>
                        ))}
                    </div>

                    {/* APPROVALS */}
                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <h2 className={styles.sectionTitle}>Pending approvals</h2>
                            <span className={styles.sectionMeta}>{tasks.length} waiting</span>
                        </div>
                        {tasks.slice(0, 4).map((task) => {
                            const due = formatDue(task.dueAt);
                            return (
                                <button key={task._id} className={styles.approvalRow} onClick={() => navigate(buildTaskRoute(task))}>
                                    <div className={styles.approvalAvatar}>{initials(task.candidateName)}</div>
                                    <div>
                                        <div className={styles.approvalName}>
                                            <span className={`${styles.approvalKindMark} ${task.priority === 'low' ? styles.approvalKindMarkLow : ''}`} />
                                            {task.candidateName}
                                        </div>
                                        <div className={styles.approvalSub}>{task.jobTitle} · {task.kind === 'cv_review' ? 'CV review' : 'AI interview review'}</div>
                                    </div>
                                    {due && <span className={`${styles.approvalDue} ${due.overdue ? styles.approvalDueOverdue : ''}`}>{due.text}</span>}
                                </button>
                            );
                        })}
                        {tasks.length === 0 && <div className={styles.empty}>None pending.</div>}
                    </section>

                    {/* ROLES */}
                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <h2 className={styles.sectionTitle}>Active roles</h2>
                            <span className={styles.sectionMeta}>{jobs.length} open</span>
                        </div>
                        {jobs.slice(0, 4).map((job) => {
                            const seed = hash(job._id);
                            const sourced = 35 + (seed % 23);
                            return (
                                <button key={job._id} className={styles.roleRow} onClick={() => navigate(`/v2/jobs/${job._id}/plan`)}>
                                    <div>
                                        <div className={styles.roleTitle}>{job.title}</div>
                                        <div className={styles.roleMeta}>{job.department || job.location || 'Workspace'}</div>
                                    </div>
                                    <div>
                                        <div className={styles.roleNumeral}>{sourced}</div>
                                        <span className={styles.roleNumeralLabel}>sourced</span>
                                    </div>
                                    <ArrowRight className={styles.roleArrow} size={18} strokeWidth={1.5} />
                                </button>
                            );
                        })}
                    </section>

                    {/* CALENDAR */}
                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <h2 className={styles.sectionTitle}>Today</h2>
                            <span className={styles.sectionMeta}>{events.length === 0 ? 'no events' : `${events.length} upcoming`}</span>
                        </div>
                        <div className={styles.dayList}>
                            {events.slice(0, 3).map((ev) => {
                                const who = ev.attendees?.[0]?.name || ev.attendees?.[0]?.email?.split('@')[0] || ev.organizer?.name || '';
                                return (
                                    <div key={ev._id} className={styles.dayRow}>
                                        <span className={styles.dayTime}>{formatTime(ev.startDateTime)}</span>
                                        <div>
                                            <div className={styles.dayTitle}>{ev.title}</div>
                                            {who && <div className={styles.daySub}>{who}</div>}
                                        </div>
                                        {ev.meetingUrl ? (
                                            <a href={ev.meetingUrl} target="_blank" rel="noopener noreferrer" className={styles.dayCta}>
                                                Join →
                                            </a>
                                        ) : (
                                            <button className={styles.dayCta} onClick={() => navigate('/v2/calendar')}>Open →</button>
                                        )}
                                    </div>
                                );
                            })}
                            {events.length === 0 && <div className={styles.empty}>Nothing scheduled.</div>}
                        </div>
                    </section>
                </div>

                <aside className={styles.rail}>
                    <div className={styles.railHead}>Activity</div>
                    <div className={styles.railList}>
                        {activity.slice(0, 8).map((ev) => (
                            <div key={ev.id} className={styles.railRow}>
                                <span className={`${styles.railDot} ${styles[ev.iconName]}`} />
                                <div>
                                    <div className={styles.railText}>{renderActivity(ev.text)}</div>
                                    <div className={styles.railTime}>{relativeFrom(ev.at)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </aside>
            </div>
        </main>
    );
}
